import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Button,
  Space,
  message,
  Modal,
  Table,
  Form,
  Input,
  Col,
  Tag
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  SettingOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../components/common/FilterPanel';
import * as accountService from '../../services/account/accountService';
import * as roleService from '../../services/role/roleService';

/**
 * Account Store Data Permission Authorization Drawer
 * @description 两个 Tab：已授权门店 / 未授权门店
 *  - 未授权 Tab：支持筛选 + 多选 + 批量 Add Authorize
 *  - 已授权 Tab：支持筛选 + 多选 + 批量 Remove Authorize（含二次确认）
 */
const AccountStoreAuthDrawer = ({ visible, account, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const loginId = account?.loginId;

  const [authorizedForm] = Form.useForm();
  const [unauthorizedForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Separate data for each tab
  const [authorizedList, setAuthorizedList] = useState([]);
  const [unauthorizedList, setUnauthorizedList] = useState([]);
  const [authorizedTotal, setAuthorizedTotal] = useState(0);
  const [unauthorizedTotal, setUnauthorizedTotal] = useState(0);

  // Pagination state for each tab (server-side)
  const [authorizedPagination, setAuthorizedPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [unauthorizedPagination, setUnauthorizedPagination] = useState({ currentPage: 1, pageSize: 10 });

  // Search filter state for each tab
  const [authorizedFilter, setAuthorizedFilter] = useState({ storeName: '', storeCode: '' });
  const [unauthorizedFilter, setUnauthorizedFilter] = useState({ storeName: '', storeCode: '' });

  // Selected rows for each tab
  const [authorizedSelectedKeys, setAuthorizedSelectedKeys] = useState([]);
  const [unauthorizedSelectedKeys, setUnauthorizedSelectedKeys] = useState([]);

  // Active tab
  const [activeTab, setActiveTab] = useState('authorized');

  // Track whether any change happened, to notify parent on close
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (visible && loginId) {
      loadAuthorizedData();
    } else if (!visible) {
      // Reset state when closed
      setAuthorizedList([]);
      setUnauthorizedList([]);
      setAuthorizedTotal(0);
      setUnauthorizedTotal(0);
      setAuthorizedPagination({ currentPage: 1, pageSize: 10 });
      setUnauthorizedPagination({ currentPage: 1, pageSize: 10 });
      setAuthorizedFilter({ storeName: '', storeCode: '' });
      setUnauthorizedFilter({ storeName: '', storeCode: '' });
      setAuthorizedSelectedKeys([]);
      setUnauthorizedSelectedKeys([]);
      setChanged(false);
      setActiveTab('authorized');
      authorizedForm.resetFields();
      unauthorizedForm.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, loginId]);

  // Load authorized stores (authQueryType: "1")
  const loadAuthorizedData = async (pagination = authorizedPagination, filter = authorizedFilter) => {
    setLoading(true);
    try {
      const res = await roleService.pageRoles({
        type: 'DATA',
        authedAccount: loginId,
        authQueryType: '1',
        nameLike: filter.storeName || undefined,
        code: filter.storeCode || undefined,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize
      });
      if (res.success && res.data) {
        setAuthorizedList(res.data.tableData || []);
        setAuthorizedTotal(res.data.paging?.totalCount ?? 0);
      } else {
        message.error(res.errorMessage || t('account.loadStorePermissionsFailed'));
        setAuthorizedList([]);
        setAuthorizedTotal(0);
      }
    } catch (error) {
      console.error('Load authorized stores failed:', error);
      message.error(t('account.loadStorePermissionsFailedRetry'));
      setAuthorizedList([]);
      setAuthorizedTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Load unauthorized stores (authQueryType: "2")
  const loadUnauthorizedData = async (pagination = unauthorizedPagination, filter = unauthorizedFilter) => {
    setLoading(true);
    try {
      const res = await roleService.pageRoles({
        type: 'DATA',
        authedAccount: loginId,
        authQueryType: '2',
        removeOldAuth: 'false',
        nameLike: filter.storeName || undefined,
        code: filter.storeCode || undefined,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize
      });
      if (res.success && res.data) {
        setUnauthorizedList(res.data.tableData || []);
        setUnauthorizedTotal(res.data.paging?.totalCount ?? 0);
      } else {
        message.error(res.errorMessage || t('account.loadStorePermissionsFailed'));
        setUnauthorizedList([]);
        setUnauthorizedTotal(0);
      }
    } catch (error) {
      console.error('Load unauthorized stores failed:', error);
      message.error(t('account.loadStorePermissionsFailedRetry'));
      setUnauthorizedList([]);
      setUnauthorizedTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab switch: load corresponding data
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'authorized') {
      loadAuthorizedData(authorizedPagination, authorizedFilter);
    } else {
      loadUnauthorizedData(unauthorizedPagination, unauthorizedFilter);
    }
  };

  // Handle authorized table pagination change
  const handleAuthorizedTableChange = (paginationConfig) => {
    const newPagination = { currentPage: paginationConfig.current, pageSize: paginationConfig.pageSize };
    setAuthorizedPagination(newPagination);
    loadAuthorizedData(newPagination, authorizedFilter);
  };

  // Handle unauthorized table pagination change
  const handleUnauthorizedTableChange = (paginationConfig) => {
    const newPagination = { currentPage: paginationConfig.current, pageSize: paginationConfig.pageSize };
    setUnauthorizedPagination(newPagination);
    loadUnauthorizedData(newPagination, unauthorizedFilter);
  };

  // Handle authorized filter
  const handleAuthorizedFilter = (values) => {
    const newFilter = { storeName: values.storeName || '', storeCode: values.storeCode || '' };
    const newPagination = { currentPage: 1, pageSize: authorizedPagination.pageSize };
    setAuthorizedFilter(newFilter);
    setAuthorizedPagination(newPagination);
    setAuthorizedSelectedKeys([]);
    loadAuthorizedData(newPagination, newFilter);
  };

  const handleAuthorizedReset = () => {
    const newFilter = { storeName: '', storeCode: '' };
    const newPagination = { currentPage: 1, pageSize: authorizedPagination.pageSize };
    setAuthorizedFilter(newFilter);
    setAuthorizedPagination(newPagination);
    setAuthorizedSelectedKeys([]);
    loadAuthorizedData(newPagination, newFilter);
  };

  // Handle unauthorized filter
  const handleUnauthorizedFilter = (values) => {
    const newFilter = { storeName: values.storeName || '', storeCode: values.storeCode || '' };
    const newPagination = { currentPage: 1, pageSize: unauthorizedPagination.pageSize };
    setUnauthorizedFilter(newFilter);
    setUnauthorizedPagination(newPagination);
    setUnauthorizedSelectedKeys([]);
    loadUnauthorizedData(newPagination, newFilter);
  };

  const handleUnauthorizedReset = () => {
    const newFilter = { storeName: '', storeCode: '' };
    const newPagination = { currentPage: 1, pageSize: unauthorizedPagination.pageSize };
    setUnauthorizedFilter(newFilter);
    setUnauthorizedPagination(newPagination);
    setUnauthorizedSelectedKeys([]);
    loadUnauthorizedData(newPagination, newFilter);
  };

  // Table columns (shared structure)
  const buildColumns = () => [
    {
      title: t('account.storeName'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: t('account.storeCode'),
      dataIndex: 'code',
      key: 'code',
      width: 200
    },
    {
      title: t('account.storePermission'),
      dataIndex: 'name',
      key: 'permission',
      render: (name) => <Tag color="blue">{name}</Tag>
    }
  ];

  // Add Authorize handler
  const handleAddAuthorize = () => {
    if (unauthorizedSelectedKeys.length === 0) {
      message.warning(t('account.selectAtLeastOne'));
      return;
    }

    Modal.confirm({
      title: t('account.addAuthorizeConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('account.addAuthorizeConfirmContent', { count: unauthorizedSelectedKeys.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setProcessing(true);
        try {
          const response = await accountService.assignRoles({
            loginId,
            roleCodeSet: unauthorizedSelectedKeys
          });
          if (response && response.success) {
            message.success(t('account.assignStorePermissionsSuccess'));
            setUnauthorizedSelectedKeys([]);
            setChanged(true);
            // Reload current page
            await loadUnauthorizedData(unauthorizedPagination, unauthorizedFilter);
          } else {
            message.error(response?.errorMessage || t('account.assignStorePermissionsFailed'));
          }
        } catch (error) {
          console.error('Add authorize failed:', error);
          message.error(t('account.assignStorePermissionsFailed'));
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  // Remove Authorize handler
  const handleRemoveAuthorize = () => {
    if (authorizedSelectedKeys.length === 0) {
      message.warning(t('account.selectAtLeastOne'));
      return;
    }

    Modal.confirm({
      title: t('account.removeAuthorizeConfirmTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('account.removeAuthorizeConfirmContent', { count: authorizedSelectedKeys.length }),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        setProcessing(true);
        try {
          const response = await accountService.removeRoles({
            loginId,
            removeRoleCodeSet: authorizedSelectedKeys
          });
          if (response && response.success) {
            message.success(t('account.assignStorePermissionsSuccess'));
            setAuthorizedSelectedKeys([]);
            setChanged(true);
            // Reload current page
            await loadAuthorizedData(authorizedPagination, authorizedFilter);
          } else {
            message.error(response?.errorMessage || t('account.removeRolesFailed'));
          }
        } catch (error) {
          console.error('Remove authorize failed:', error);
          message.error(t('account.removeRolesFailed'));
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handleClose = () => {
    if (changed && onSuccess) {
      onSuccess();
    } else if (onCancel) {
      onCancel();
    }
  };

  // Render Authorized Tab content
  const renderAuthorizedTab = () => (
    <>
      <FilterPanel
        form={authorizedForm}
        onFilter={handleAuthorizedFilter}
        onReset={handleAuthorizedReset}
        initialValues={{}}
      >
        <Col span={8}>
          <Form.Item name="storeName" label={t('account.storeName')}>
            <Input placeholder={t('common.pleaseInput')} allowClear />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="storeCode" label={t('account.storeCode')}>
            <Input placeholder={t('common.pleaseInput')} allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      <div style={{ margin: '16px 0' }}>
        <Space>
          <span>
            {t('account.selectTotal', { selected: authorizedSelectedKeys.length, total: authorizedTotal })}
          </span>
          <Button
            danger
            icon={<MinusCircleOutlined />}
            disabled={authorizedSelectedKeys.length === 0}
            loading={processing}
            onClick={handleRemoveAuthorize}
          >
            {t('account.removeAuthorize')}
          </Button>
        </Space>
      </div>

      <Table
        rowKey="code"
        size="middle"
        loading={loading}
        columns={buildColumns()}
        dataSource={authorizedList}
        pagination={{
          current: authorizedPagination.currentPage,
          pageSize: authorizedPagination.pageSize,
          total: authorizedTotal,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`
        }}
        onChange={handleAuthorizedTableChange}
        rowSelection={{
          selectedRowKeys: authorizedSelectedKeys,
          onChange: (keys) => setAuthorizedSelectedKeys(keys)
        }}
      />
    </>
  );

  // Render Unauthorized Tab content
  const renderUnauthorizedTab = () => (
    <>
      <FilterPanel
        form={unauthorizedForm}
        onFilter={handleUnauthorizedFilter}
        onReset={handleUnauthorizedReset}
        initialValues={{}}
      >
        <Col span={8}>
          <Form.Item name="storeName" label={t('account.storeName')}>
            <Input placeholder={t('common.pleaseInput')} allowClear />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="storeCode" label={t('account.storeCode')}>
            <Input placeholder={t('common.pleaseInput')} allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      <div style={{ margin: '16px 0' }}>
        <Space>
          <span>
            {t('account.selectTotal', { selected: unauthorizedSelectedKeys.length, total: unauthorizedTotal })}
          </span>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={unauthorizedSelectedKeys.length === 0}
            loading={processing}
            onClick={handleAddAuthorize}
          >
            {t('account.addAuthorize')}
          </Button>
        </Space>
      </div>

      <Table
        rowKey="code"
        size="middle"
        loading={loading}
        columns={buildColumns()}
        dataSource={unauthorizedList}
        pagination={{
          current: unauthorizedPagination.currentPage,
          pageSize: unauthorizedPagination.pageSize,
          total: unauthorizedTotal,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`
        }}
        onChange={handleUnauthorizedTableChange}
        rowSelection={{
          selectedRowKeys: unauthorizedSelectedKeys,
          onChange: (keys) => setUnauthorizedSelectedKeys(keys)
        }}
      />
    </>
  );

  const tabItems = [
    {
      key: 'authorized',
      label: t('account.authorizedStores'),
      children: renderAuthorizedTab()
    },
    {
      key: 'unauthorized',
      label: t('account.unauthorizedStores'),
      children: renderUnauthorizedTab()
    }
  ];

  return (
    <Drawer
      title={
        <Space>
          <SettingOutlined style={{ color: '#1890ff' }} />
          {t('account.storePermissionAuthorizationTitle', { accountName: account?.name || loginId || '' })}
        </Space>
      }
      open={visible}
      onClose={handleClose}
      width="70vw"
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button type="primary" onClick={handleClose}>
            {t('common.back')}
          </Button>
        </div>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
    </Drawer>
  );
};

export default AccountStoreAuthDrawer;
